import React, { useEffect, useMemo, useRef } from "react";
import { RouteComponentProps } from "react-router-dom";
import { CallState, StayTypesEnum, VIEWED_VR_DETAILS } from "redmond";
import queryStringParser from "query-string";
import { Box } from "@material-ui/core";
import { useDeviceTypes } from "halifax";
import {
  travelAppInterface,
  TravelAppInterfaceEventName,
  isCorpTenant,
  shouldCheckLifestyleEligibility,
} from "@capone/common";
import { VacationRentalShopConnectorProps } from "./container";
import { config } from "../../api/config";
import {
  useExperiments,
  VR_FOR_PREMIUM_CARDHOLDERS_EXPERIMENT,
  AVAILABLE,
  getExperimentVariant,
  VR_FOR_NON_PREMIUM_CARDHOLDERS_EXPERIMENT,
  VR_CALENDAR_EXPERIMENT,
  LUXURY_VACATION_RENTALS_REFRESH,
} from "../../context/experiments";
import { PATH_VACATION_RENTALS_SHOP } from "../../utils/paths";
import { VRDesktopShop } from "./components/VRDesktopShop";
import { VRShop } from "./components/VRShop";
import { VRMobileShop } from "./components/VRMobileShop";
import { trackEvent } from "../../api/v0/analytics/trackEvent";

export interface IVacationRentalShopProps
  extends VacationRentalShopConnectorProps,
    RouteComponentProps {}

export const VacationRentalShop = (props: IVacationRentalShopProps) => {
  const {
    history,
    fetchVacationRentalShop,
    setStayType,
    fetchedVacationRentalDetails,
    viewedVacationRentalDetailsProperties,
    listPaymentMethods,
    fetchTravelWalletDetails,
    checkLifestyleEligibility,
    setLifestyleEligibility,
    isLifestyleEligible,
    largestValueAccount,
    rewardsAccounts,
  } = props;
  const { matchesDesktop, matchesMobile } = useDeviceTypes();
  const expState = useExperiments();

  const VRForPremiumCardHolders = getExperimentVariant(
    expState.experiments,
    VR_FOR_PREMIUM_CARDHOLDERS_EXPERIMENT
  );
  const isVRForPremiumCardHoldersEnabled = useMemo(
    () => VRForPremiumCardHolders === AVAILABLE,
    [VRForPremiumCardHolders]
  );

  const VRRefresh = getExperimentVariant(
    expState.experiments,
    LUXURY_VACATION_RENTALS_REFRESH
  );
  const isVRRefresh = useMemo(() => VRRefresh === AVAILABLE, [VRRefresh]);

  const VRForNonPremiumCardHolders = getExperimentVariant(
    expState.experiments,
    VR_FOR_NON_PREMIUM_CARDHOLDERS_EXPERIMENT
  );
  const isVRForNonPremiumCardHoldersEnabled = useMemo(
    () => VRForNonPremiumCardHolders === AVAILABLE,
    [VRForNonPremiumCardHolders]
  );

  const VRCalendar = getExperimentVariant(
    expState.experiments,
    VR_CALENDAR_EXPERIMENT
  );

  const isVRCalendarEnabled = useMemo(
    () => VRCalendar === AVAILABLE,
    [VRCalendar]
  );

  const queryString = useMemo(
    () => queryStringParser.parse(history.location.search),
    [history.location.search]
  );

  const hasPageLoadedEventFired = useRef(false);

  useEffect(() => {
    fetchTravelWalletDetails();
    listPaymentMethods();
  }, []);

  // Check eligibility once rewards accounts have loaded
  useEffect(() => {
    // Wait for rewards accounts to load before checking eligibility
    if (rewardsAccounts.length === 0) {
      return;
    }

    // Only check eligibility for corporate tenant with eligible cards
    const isCorporate = isCorpTenant(config.TENANT);
    if (shouldCheckLifestyleEligibility(isCorporate, rewardsAccounts)) {
      checkLifestyleEligibility();
    } else {
      // Set to false immediately if not checking
      setLifestyleEligibility(false);
    }
  }, [rewardsAccounts, checkLifestyleEligibility, setLifestyleEligibility]);

  useEffect(() => {
    if (fetchedVacationRentalDetails) {
      trackEvent({
        eventName: VIEWED_VR_DETAILS,
        ...viewedVacationRentalDetailsProperties,
      });

      if (!hasPageLoadedEventFired.current) {
        travelAppInterface.sendEvent({
          name: TravelAppInterfaceEventName.PAGE_LOADED,
        });
        hasPageLoadedEventFired.current = true;
      }
    }
  }, [fetchedVacationRentalDetails]);

  useEffect(() => {
    const isEligibleViaExperiments =
      isVRForPremiumCardHoldersEnabled || isVRForNonPremiumCardHoldersEnabled;
    const isEligibleViaAPI = isLifestyleEligible === true;

    if (
      history.location.pathname === PATH_VACATION_RENTALS_SHOP &&
      (isEligibleViaExperiments || isEligibleViaAPI)
    ) {
      setStayType(StayTypesEnum.VacationRentals);
    } else {
      setStayType(StayTypesEnum.Hotels);
    }
  }, [
    history.location.pathname,
    isVRForPremiumCardHoldersEnabled,
    isVRForNonPremiumCardHoldersEnabled,
    isLifestyleEligible,
  ]);

  useEffect(() => {
    // Wait for experiments to load before proceeding
    if (
      ![CallState.Success, CallState.Failed].includes(expState.callState) &&
      expState.experiments.length === 0
    ) {
      return;
    }

    if (
      [CallState.Success, CallState.Failed].includes(expState.callState) ||
      expState.experiments.length > 0
    ) {
      // Check if user is eligible via experiments
      const isEligibleViaExperiments =
        isVRForPremiumCardHoldersEnabled || isVRForNonPremiumCardHoldersEnabled;

      // If eligibility is still loading (null) and no experiment grants access, wait for it
      const isEligibilityStillLoading = isLifestyleEligible === null && !isEligibleViaExperiments;

      // Always fetch VR shop - eligibility only affects styling/benefits display
      if (!isEligibilityStillLoading) {
        fetchVacationRentalShop(
          history,
          {
            overrideStateByQueryParams: true,
            fetchListingCalendar: isVRCalendarEnabled,
          },
          isVRForPremiumCardHoldersEnabled
        );
      }
      setTimeout(() => window.scrollTo(0, 0), 0);
    }
  }, [expState.callState, queryString.listingId, isLifestyleEligible]);

  return (
    <Box className="vacation-rental-shop-root">
      {matchesDesktop && !isVRRefresh && (
        <VRDesktopShop
          isVRForPremiumCardHoldersEnabled={isVRForPremiumCardHoldersEnabled}
          largestValueAccount={largestValueAccount}
        />
      )}
      {matchesMobile && !isVRRefresh && (
        <VRMobileShop
          isVRForPremiumCardHoldersEnabled={isVRForPremiumCardHoldersEnabled}
          largestValueAccount={largestValueAccount}
        />
      )}
      {isVRRefresh && (
        <VRShop
          isVRForPremiumCardHoldersEnabled={isVRForPremiumCardHoldersEnabled}
          largestValueAccount={largestValueAccount}
        />
      )}
    </Box>
  );
};
